import {
    api,
    ApiError,
    apiErrorMessage,
    describeComponents,
    describeConditions,
    describeTerms,
    EVENT_LABELS,
    oneSpec,
    PricingSimulator,
    type RuleDraft,
    RuleEditor,
    rulesErrors,
    type SimulationInput,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    PageHeader,
    StatusBadge,
    UnsavedGuard,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import type {
    PricingCard,
    PricingCardWithVersions,
    PricingRule,
    PricingSimulation,
} from '@/api/types';
import { DetailBody } from '@/components/detail';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { CardLabelBadge, CardScope } from '@/pages/pricing/cards';

/** The reads of `/pricing/cards/:id` (BM-46 §2.0; a past version is read when chosen). */
export const cardReads = (id: string | undefined) => ({
    card: oneSpec(`/staff/cards/${id ?? ''}`),
});

const draftOf = (rules: readonly PricingRule[]): RuleDraft[] =>
    rules.map((r) => ({
        event: r.event,
        kind: r.kind,
        name: r.name,
        conditions: { ...r.conditions },
        components: { ...r.components },
        terms: { ...r.terms },
    }));

export function CardPage() {
    const { id = '' } = useParams();
    return <CardDetail key={id} id={id} />;
}

function CardDetail({ id }: { id: string }) {
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    const [viewing, setViewing] = useState<number | null>(null);
    const query = useOne<PricingCardWithVersions>(
        cardReads(id).card.path,
        viewing === null ? {} : { version: viewing },
    );
    const [draft, setDraft] = useState<RuleDraft[] | null>(null);
    const [errors, setErrors] = useState<{
        card: string[];
        byRule: Record<number, string[]>;
    }>({ card: [], byRule: {} });
    const save = useWrite(
        async (body: { version: number; rules: RuleDraft[] }) =>
            (await api().post<PricingCard>(`/staff/cards/${id}/versions`, body))
                .data,
        ['/staff/cards'],
    );
    const onError = (e: unknown) =>
        setErrors(
            ApiError.is(e) && e.status === 422
                ? rulesErrors(
                      e.errors.map((x) => ({
                          field: x.field,
                          message: x.message ?? x.key,
                      })),
                  )
                : { card: [apiErrorMessage(e)], byRule: {} },
        );
    return (
        <div>
            <UnsavedGuard dirty={draft !== null} />
            <DetailBody query={query} title="Pricing card">
                {(c) => {
                    const open = c.active_to === null;
                    const editable = open && can(P.pricingManage);
                    const rules = draft ?? draftOf(c.rules);
                    const simulate = async (input: SimulationInput) =>
                        (
                            await api().post<PricingSimulation>(
                                `/staff/cards/${id}/simulate`,
                                {
                                    ...input,
                                    ...(draft !== null
                                        ? { rules: draft }
                                        : { version: c.version }),
                                },
                            )
                        ).data;
                    return (
                        <div className="space-y-4">
                            <PageHeader
                                crumbs={[
                                    {
                                        label: 'Pricing cards',
                                        href: '/pricing/cards',
                                    },
                                ]}
                                title={c.name}
                                meta={
                                    <span className="flex flex-wrap items-center gap-2">
                                        <StatusBadge
                                            status={c.side}
                                            domain={ADMIN_STATUSES.cardSide}
                                        />
                                        <CardLabelBadge label={c.label} />
                                        <Badge variant="neutral">
                                            v{c.version}
                                            {open ? '' : ' (closed)'}
                                        </Badge>
                                        <CardScope merchantId={c.merchant_id} />
                                        <AsOfBadge
                                            asOf={query.freshness.asOf}
                                        />
                                    </span>
                                }
                            />
                            {errors.card.length ? (
                                <ul
                                    role="alert"
                                    className="text-destructive text-sm"
                                >
                                    {errors.card.map((m, i) => (
                                        <li key={i}>{m}</li>
                                    ))}
                                </ul>
                            ) : null}
                            <Card>
                                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                                    <CardTitle className="text-sm font-medium">
                                        Rules
                                    </CardTitle>
                                    {editable ? (
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={draft === null}
                                                onClick={() => {
                                                    setDraft(null);
                                                    setErrors({
                                                        card: [],
                                                        byRule: {},
                                                    });
                                                }}
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                size="sm"
                                                data-testid="save-version"
                                                disabled={
                                                    draft === null ||
                                                    save.isPending
                                                }
                                                onClick={() =>
                                                    save.mutate(
                                                        {
                                                            version: c.version,
                                                            rules,
                                                        },
                                                        {
                                                            onSuccess: () => {
                                                                setDraft(null);
                                                                setViewing(
                                                                    null,
                                                                );
                                                                setErrors({
                                                                    card: [],
                                                                    byRule: {},
                                                                });
                                                            },
                                                            onError,
                                                        },
                                                    )
                                                }
                                            >
                                                Save as v{c.version + 1}
                                            </Button>
                                        </div>
                                    ) : null}
                                </CardHeader>
                                <CardContent>
                                    {editable ? (
                                        <RuleEditor
                                            host="payments"
                                            side={c.side}
                                            rules={rules}
                                            onChange={setDraft}
                                            errors={errors.byRule}
                                        />
                                    ) : (
                                        <ol
                                            className="divide-y text-sm"
                                            data-testid="card-rules"
                                        >
                                            {c.rules.map((r) => (
                                                <li
                                                    key={r.position}
                                                    className="py-2"
                                                >
                                                    <span className="font-medium">
                                                        #{r.position}{' '}
                                                        {EVENT_LABELS[
                                                            r.event
                                                        ] ?? r.event}
                                                        {r.kind === 'addon'
                                                            ? ' add-on'
                                                            : ''}
                                                        {r.name
                                                            ? ` · ${r.name}`
                                                            : ''}
                                                    </span>
                                                    <span className="text-fg-2 block">
                                                        {describeConditions(
                                                            r.conditions,
                                                        )}
                                                        :{' '}
                                                        {describeComponents(
                                                            r.components,
                                                        )}
                                                        {describeTerms(
                                                            r.event,
                                                            r.terms,
                                                        )
                                                            ? `; ${describeTerms(r.event, r.terms)}`
                                                            : ''}
                                                    </span>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium">
                                        Simulator
                                        {draft !== null
                                            ? ' (unsaved rules)'
                                            : ` (v${c.version})`}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <PricingSimulator
                                        host="payments"
                                        onSimulate={simulate}
                                    />
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium">
                                        Versions
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul
                                        className="divide-y text-sm"
                                        data-testid="card-versions"
                                    >
                                        {c.versions.map((v) => (
                                            <li
                                                key={v.version}
                                                className="flex flex-wrap items-center justify-between gap-2 py-2"
                                            >
                                                <span>
                                                    <span className="font-medium">
                                                        v{v.version}
                                                    </span>{' '}
                                                    {v.name} · from{' '}
                                                    <DateTime
                                                        value={v.active_from}
                                                        empty="—"
                                                    />
                                                    {v.active_to ? (
                                                        <>
                                                            {' '}
                                                            to{' '}
                                                            <DateTime
                                                                value={
                                                                    v.active_to
                                                                }
                                                                empty="—"
                                                            />
                                                        </>
                                                    ) : (
                                                        ' (open)'
                                                    )}{' '}
                                                    · {who(v.created_by, me)}
                                                </span>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    disabled={
                                                        v.version ===
                                                            c.version ||
                                                        draft !== null
                                                    }
                                                    onClick={() =>
                                                        setViewing(
                                                            v.active_to
                                                                ? v.version
                                                                : null,
                                                        )
                                                    }
                                                >
                                                    {v.version === c.version
                                                        ? 'Shown'
                                                        : 'Show'}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        </div>
                    );
                }}
            </DetailBody>
        </div>
    );
}
